import { useEffect } from 'react';
import theme from './index';

/**
 * ThemeProvider
 *
 * Reads the theme config and injects all values as CSS custom properties
 * on the :root element. This makes every CSS variable in style.css
 * dynamically controlled from theme/index.js.
 *
 * To switch themes at runtime, pass a `customTheme` prop (same shape as the
 * default theme) and ThemeProvider will re-inject all variables.
 */
function ThemeProvider({ children, customTheme }) {
  const t = customTheme || theme;

  useEffect(() => {
    const root = document.documentElement;

    // Colors
    root.style.setProperty('--color-primary', t.colors.primary);
    root.style.setProperty('--color-primary-dark', t.colors.primaryDark);
    root.style.setProperty('--color-primary-light', t.colors.primaryLight);
    root.style.setProperty('--color-secondary', t.colors.secondary);
    root.style.setProperty('--color-accent', t.colors.accent);
    root.style.setProperty('--bg-deep', t.colors.bgDeep);
    root.style.setProperty('--bg-dark', t.colors.bgDark);
    root.style.setProperty('--bg-medium', t.colors.bgMedium);
    root.style.setProperty('--bg-card', t.colors.bgCard);
    root.style.setProperty('--bg-card-hover', t.colors.bgCardHover);
    root.style.setProperty('--text-primary', t.colors.textPrimary);
    root.style.setProperty('--text-secondary', t.colors.textSecondary);
    root.style.setProperty('--text-muted', t.colors.textMuted);
    root.style.setProperty('--border-color', t.colors.borderColor);
    root.style.setProperty('--border-hover', t.colors.borderHover);
    root.style.setProperty('--scrollbar-track', t.colors.scrollbarTrack);
    root.style.setProperty('--scrollbar-thumb', t.colors.scrollbarThumb);
    root.style.setProperty('--scrollbar-thumb-hover', t.colors.scrollbarThumbHover);
    root.style.setProperty('--color-footer', t.colors.footer);
    root.style.setProperty('--social-icon-bg', t.colors.socialIconBg);
    root.style.setProperty('--social-icon-hover-bg', t.colors.socialIconHoverBg);
    root.style.setProperty('--social-icon-hover-glow', t.colors.socialIconHoverGlow);
    root.style.setProperty('--social-icon-color', t.colors.socialIconColor);

    // Gradients
    root.style.setProperty('--gradient-body', t.gradients.body);
    root.style.setProperty('--gradient-section', t.gradients.section);
    root.style.setProperty('--gradient-image', t.gradients.image);
    root.style.setProperty('--gradient-navbar-sticky', t.gradients.navbarSticky);
    root.style.setProperty('--gradient-navbar-mobile', t.gradients.navbarMobile);

    // Typography
    root.style.setProperty('--font-family', t.typography.fontFamily);
    root.style.setProperty('--font-size-base', t.typography.fontSizeBase);
    root.style.setProperty('--line-height-base', t.typography.lineHeightBase);

    // Spacing
    root.style.setProperty('--nav-height', t.spacing.navHeight);
    root.style.setProperty('--section-pt', t.spacing.sectionPaddingTop);
    root.style.setProperty('--section-pb', t.spacing.sectionPaddingBottom);

    // Borders
    root.style.setProperty('--radius-xs', t.borders.radiusXs);
    root.style.setProperty('--radius-sm', t.borders.radiusSm);
    root.style.setProperty('--radius-md', t.borders.radiusMd);
    root.style.setProperty('--radius-lg', t.borders.radiusLg);
    root.style.setProperty('--radius-xl', t.borders.radiusXl);
    root.style.setProperty('--radius-full', t.borders.radiusFull);

    // Shadows
    root.style.setProperty('--shadow-card', t.shadows.card);
    root.style.setProperty('--shadow-card-hover', t.shadows.cardHover);
    root.style.setProperty('--shadow-glow', t.shadows.glow);
    root.style.setProperty('--shadow-glow-strong', t.shadows.glowStrong);
    root.style.setProperty('--shadow-icon', t.shadows.icon);
    root.style.setProperty('--shadow-navbar', t.shadows.navbar);
    root.style.setProperty('--shadow-social-before', t.shadows.socialBefore);
    root.style.setProperty('--shadow-social-hover', t.shadows.socialHover);

    // Transitions
    root.style.setProperty('--transition-fast', t.transitions.fast);
    root.style.setProperty('--transition-normal', t.transitions.normal);
    root.style.setProperty('--transition-slow', t.transitions.slow);
  }, [t]);

  return children;
}

export default ThemeProvider;
